import { CheckOutlined } from '@ant-design/icons';
import { Button, Flex, List, Space, Tag, Typography } from 'antd';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { INVENTORIZE_REVIEW_STATUSES } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { reviewStatusLabel, reviewText } from '@/features/inventorization/review-i18n';
import { readInventorizeReviewStatusTagColor } from '@/routers/inventorization/components/ReviewStatusTag';

export function ReviewFieldList({
  fields,
  getLabel = field => field.label,
  onApproveField,
  onOpenField,
}: {
  fields: InventorizeReviewField[];
  getLabel?: (field: InventorizeReviewField) => string;
  onApproveField?: (field: InventorizeReviewField) => void;
  onOpenField: (field: InventorizeReviewField) => void;
}) {
  return (
    <List
      dataSource={fields}
      renderItem={field => (
        <List.Item
          data-review-field-id={field.id}
          onClick={() => onOpenField(field)}
          role="button"
          style={{ cursor: 'pointer' }}
          tabIndex={0}
        >
          <List.Item.Meta
            description={
              <Space direction="vertical" size={2}>
                <Typography.Text type={field.value ? undefined : 'secondary'}>
                  {field.displayValue ?? (field.value || reviewText('field.emptyValue', 'Empty'))}
                </Typography.Text>
              </Space>
            }
            title={
              <Flex align="center" gap="small" wrap>
                <Typography.Text strong>
                  {getLabel(field)}
                  {field.required ? (
                    <Typography.Text aria-label="required" type="danger">
                      {' *'}
                    </Typography.Text>
                  ) : null}
                </Typography.Text>
                <ReviewFieldStatusTag field={field} />
                {onApproveField && isAiSuggestion(field) ? (
                  <Button
                    icon={<CheckOutlined />}
                    onClick={event => {
                      event.stopPropagation();
                      onApproveField(field);
                    }}
                    size="small"
                  >
                    {reviewText('actions.approveAi', 'Approve')}
                  </Button>
                ) : null}
              </Flex>
            }
          />
        </List.Item>
      )}
    />
  );
}

function isAiSuggestion(field: InventorizeReviewField) {
  return (
    Boolean(field.value) &&
    field.sourceKind === 'ai' &&
    (field.status === INVENTORIZE_REVIEW_STATUSES.parsed || field.status === INVENTORIZE_REVIEW_STATUSES.unsure)
  );
}

function ReviewFieldStatusTag({ field }: { field: InventorizeReviewField }) {
  if (field.status === INVENTORIZE_REVIEW_STATUSES.empty) {
    return null;
  }

  const formattedSource = formatReviewSource(field);
  const confidence = typeof field.confidence === 'number' ? ` ${Math.round(field.confidence * 100)}%` : '';
  const source = formattedSource ? ` · ${formattedSource}` : '';

  return (
    <Tag color={readInventorizeReviewStatusTagColor(field.status)}>
      {`${reviewStatusLabel(field.status)}${confidence}${source}`}
    </Tag>
  );
}

function formatReviewSource(field: InventorizeReviewField) {
  if (field.sourceKind === 'ai') {
    return field.source
      ? reviewText('source.aiWithDetail', 'AI · {detail}', { detail: field.source })
      : reviewText('source.ai', 'AI');
  }

  if (field.sourceKind === 'photo' && field.source) {
    return reviewText('source.photo', 'Photo {id}', { id: field.source });
  }

  return field.source;
}
